import { API_ENDPOINTS } from '@/api/endpoints';
import { useFiscalYears } from '@/fiscalYears/useFiscalYears';
import { Project } from '@/stores/ProjectStore';
import { apiFetch } from '@/util/apiClient';
import { alpha, Box, useMediaQuery, useTheme } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiscalYearSidebar } from './landing/components/FiscalYearSidebar';
import { GridContainer } from './landing/components/GridContainer';
import { LandingPageHeader } from './landing/components/LandingPageHeader';
import { ProjectCard } from './landing/components/ProjectCard';
import {
  FilterState,
  ProjectListHeader,
} from './landing/components/ProjectListHeader';
import { getProjectStatus } from './landing/statusUtils';
import { useOrg } from './useOrg';
import { useOrganizationPerms } from './useOrganizationRole';
import {
  useUpdateUserPreferences,
  useUserPreferences,
} from './useUserPreferences';

interface Subproject {
  id: string;
  projectId: string;
  name: string;
  description: string;
  sector: string;
  status: 'not-started' | 'in-progress' | 'finished';
  createdAt: string;
  updatedAt: string;
  buildingUsages: string[];
}

interface ProjectWithSubprojects extends Project {
  subprojects?: Subproject[];
}

export function LandingPage() {
  const theme = useTheme();
  const lp = theme.palette.landingPage;
  const { selectedOrg } = useOrg();
  const navigate = useNavigate();
  const { data: fiscalYears } = useFiscalYears(selectedOrg?.id || '');
  const { data: perms } = useOrganizationPerms(selectedOrg?.id);
  const { data: userPrefs } = useUserPreferences(selectedOrg?.id);
  const updatePrefs = useUpdateUserPreferences(selectedOrg?.id);
  const queryClient = useQueryClient();

  const isSmallScreen = useMediaQuery('(max-width:1024px)');

  const [selectedFiscalYear, setSelectedFiscalYear] = useState<string>('');
  const [nameSortOrder, setNameSortOrder] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [statusSortOrder, setStatusSortOrder] = useState<'asc' | 'desc' | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    statuses: [],
    departments: [],
    fiscalYears: [],
  });
  // Get pinned projects from backend
  const pinnedProjects = useMemo(() => {
    return new Set(userPrefs?.pinnedProjects || []);
  }, [userPrefs]);
  const [openProjectDropdowns, setOpenProjectDropdowns] = useState<Set<string>>(
    new Set(),
  );
  const [showArchived] = useState(true);

  // Fetch projects from API
  const { data: rawProjects = [], refetch: refetchProjects } = useQuery({
    queryKey: ['projects', selectedOrg?.id, showArchived],
    queryFn: async () => {
      if (!selectedOrg?.id) return [];
      return apiFetch<Project[]>(
        API_ENDPOINTS.ORGANIZATIONS.PROJECTS.LIST(selectedOrg.id, showArchived),
      );
    },
    enabled: !!selectedOrg?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    placeholderData: (previousData) => previousData,
    retry: 1,
    refetchOnMount: 'always', // Always refetch when returning to landing page
    refetchOnWindowFocus: true, // Refetch when window regains focus
  });

  // Create stable project IDs list for query key (prevents query from resetting when rawProjects reference changes)
  const projectIds = useMemo(
    () =>
      rawProjects
        .map((p) => p.id)
        .sort()
        .join(','),
    [rawProjects],
  );

  // Fetch subprojects for each project
  const {
    data: projectsWithSubprojects = [],
    isFetching: isFetchingSubprojects,
  } = useQuery({
    queryKey: ['projects-with-subprojects', selectedOrg?.id, projectIds],
    queryFn: async () => {
      if (!selectedOrg?.id || !rawProjects.length) return [];

      const projectsWithSubs = await Promise.all(
        rawProjects.map(async (project) => {
          try {
            const subprojects = await apiFetch<Subproject[]>(
              API_ENDPOINTS.ORGANIZATIONS.PROJECTS.SUBPROJECTS.LIST(
                selectedOrg.id,
                project.id,
              ),
            );
            return { ...project, subprojects };
          } catch (error) {
            console.error(
              `Failed to load subprojects for project ${project.id}:`,
              error,
            );
            return { ...project, subprojects: [] };
          }
        }),
      );

      return projectsWithSubs;
    },
    enabled: !!selectedOrg?.id && rawProjects.length > 0,
    staleTime: Infinity, // Never mark as stale - only refetch when explicitly invalidated
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    placeholderData: (previousData) => previousData, // Keep showing old data while refetching
    retry: 1, // Retry once on failure
    refetchOnMount: 'always', // Always refetch when returning to landing page
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Use projects with subprojects if available, otherwise fallback to raw projects
  // Keep showing previous data while refetching to prevent disappearing subprojects
  // Don't switch to rawProjects while subprojects are loading (this prevents flickering)
  const projects =
    projectsWithSubprojects.length > 0 || isFetchingSubprojects
      ? projectsWithSubprojects
      : rawProjects;

  // Filter fiscal years to only show those with projects
  const fiscalYearsWithProjects = useMemo(() => {
    if (!fiscalYears || !projects) return [];

    const fiscalYearIdsWithProjects = new Set(
      projects.map((p) => p.fiscalYear).filter(Boolean),
    );

    return fiscalYears.filter((fy) => fiscalYearIdsWithProjects.has(fy.id));
  }, [fiscalYears, projects]);

  // Get the most recent fiscal year from the filtered list (first one, as they're sorted by name desc)
  const mostRecentFiscalYear = fiscalYearsWithProjects?.[0]?.id;

  // Get available departments from projects
  const availableDepartments = useMemo(() => {
    if (!projects) return [];
    const departments = new Set<string>();
    projects.forEach((p) => {
      if (p.departmentDetails?.name) {
        departments.add(p.departmentDetails.name);
      }
    });
    return Array.from(departments).sort();
  }, [projects]);

  // Format fiscal years for filter menu
  const availableFiscalYearsForFilter = useMemo(() => {
    return fiscalYearsWithProjects.map((fy) => ({
      id: fy.id,
      label: fy.name,
    }));
  }, [fiscalYearsWithProjects]);

  // Default to saved fiscal year or most recent fiscal year on load
  useEffect(() => {
    if (!mostRecentFiscalYear || !selectedOrg?.id) return;

    // Check localStorage for saved fiscal year (scoped to organization)
    const storageKey = `selectedFiscalYear_${selectedOrg.id}`;
    const savedFiscalYear = localStorage.getItem(storageKey);

    // If there's a saved fiscal year and it exists in the available years, use it
    if (
      savedFiscalYear &&
      fiscalYearsWithProjects.some((fy) => fy.id === savedFiscalYear)
    ) {
      setSelectedFiscalYear(savedFiscalYear);
    } else {
      // Otherwise default to most recent fiscal year
      setSelectedFiscalYear(mostRecentFiscalYear);
    }
  }, [mostRecentFiscalYear, selectedOrg?.id, fiscalYearsWithProjects]);

  // Handler for fiscal year selection
  const handleFiscalYearSelect = useCallback(
    (fiscalYearId: string) => {
      setSelectedFiscalYear(fiscalYearId);

      // Save selected fiscal year to localStorage (scoped to organization)
      if (selectedOrg?.id) {
        const storageKey = `selectedFiscalYear_${selectedOrg.id}`;
        localStorage.setItem(storageKey, fiscalYearId);
      }
    },
    [selectedOrg?.id],
  );

  const handleEditProject = (project: Project) => {
    navigate(`/project/${project.id}/proj-overview`);
  };

  // Filter projects based on selected fiscal year, search query, and filters
  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    let filtered = projects;

    // Filter by fiscal year (from sidebar)
    if (selectedFiscalYear) {
      filtered = filtered.filter((p) => p.fiscalYear === selectedFiscalYear);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.description?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Filter by status
    if (filters.statuses.length > 0) {
      filtered = filtered.filter((p) => {
        const { mainStatus } = getProjectStatus(p);
        return filters.statuses.includes(mainStatus);
      });
    }

    // Filter by department
    if (filters.departments.length > 0) {
      filtered = filtered.filter(
        (p) =>
          p.departmentDetails?.name &&
          filters.departments.includes(p.departmentDetails.name),
      );
    }

    // Filter by fiscal years (from filter menu)
    if (filters.fiscalYears.length > 0) {
      filtered = filtered.filter(
        (p) => p.fiscalYear && filters.fiscalYears.includes(p.fiscalYear),
      );
    }

    return filtered;
  }, [projects, selectedFiscalYear, searchQuery, filters]);

  // Sort projects
  const sortedProjects = useMemo(() => {
    const sorted = [...filteredProjects];

    if (nameSortOrder) {
      sorted.sort((a, b) => {
        const comparison = a.name.localeCompare(b.name);
        return nameSortOrder === 'asc' ? comparison : -comparison;
      });
    }

    if (statusSortOrder) {
      sorted.sort((a, b) => {
        const { mainStatus: aStatus } = getProjectStatus(a);
        const { mainStatus: bStatus } = getProjectStatus(b);

        const statusOrder: Record<string, number> = {
          Active: 0,
          Completed: 1,
          'Pencils Down': 2,
          Archived: 3,
        };

        const comparison =
          (statusOrder[aStatus] || 0) - (statusOrder[bStatus] || 0);
        return statusSortOrder === 'asc' ? comparison : -comparison;
      });
    }

    // Sort pinned projects to the top
    sorted.sort((a, b) => {
      const aIsPinned = pinnedProjects.has(a.id);
      const bIsPinned = pinnedProjects.has(b.id);
      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;
      return 0;
    });

    return sorted;
  }, [filteredProjects, nameSortOrder, statusSortOrder, pinnedProjects]);

  const handleNameSort = useCallback(() => {
    setNameSortOrder((prev) => {
      if (prev === null) return 'asc';
      if (prev === 'asc') return 'desc';
      return null;
    });
    setStatusSortOrder(null);
  }, []);

  const handleStatusSort = useCallback(() => {
    setStatusSortOrder((prev) => {
      if (prev === null) return 'asc';
      if (prev === 'asc') return 'desc';
      return null;
    });
    setNameSortOrder(null);
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleFiltersChange = useCallback((newFilters: FilterState) => {
    setFilters(newFilters);
  }, []);

  const handleTogglePin = useCallback(
    (projectId: string) => {
      const currentPinned = userPrefs?.pinnedProjects || [];
      const newPinned = currentPinned.includes(projectId)
        ? currentPinned.filter((id) => id !== projectId)
        : [...currentPinned, projectId];

      updatePrefs.mutate({ pinnedProjects: newPinned });
    },
    [userPrefs, updatePrefs],
  );

  const handleToggleDropdown = useCallback((projectId: string) => {
    setOpenProjectDropdowns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  }, []);

  // Helper function to refresh project data after mutations
  const refreshProjectData = useCallback(async () => {
    // Remove queries from cache
    queryClient.removeQueries({ queryKey: ['projects-with-subprojects'] });
    queryClient.removeQueries({ queryKey: ['projects'] });

    // Explicitly refetch projects to trigger update
    await refetchProjects();
  }, [queryClient, refetchProjects]);

  const handleArchive = useCallback(
    async (projectId: string) => {
      if (!selectedOrg?.id) return;

      try {
        await apiFetch(
          `${API_ENDPOINTS.ORGANIZATIONS.PROJECTS.DETAIL(selectedOrg.id, projectId)}?archive=true`,
          {
            method: 'DELETE',
          },
        );
        await refreshProjectData();
      } catch (error) {
        console.error('Failed to archive project:', error);
      }
    },
    [selectedOrg?.id, refreshProjectData],
  );

  const handleDelete = useCallback(
    async (projectId: string) => {
      if (!selectedOrg?.id) return;

      try {
        const projectToDelete = projects?.find((p) => p.id === projectId);

        // If project is archived, restore it first before deleting
        if (projectToDelete?.deletedAt) {
          await apiFetch(
            API_ENDPOINTS.ORGANIZATIONS.PROJECTS.RESTORE(
              selectedOrg.id,
              projectId,
            ),
            {
              method: 'POST',
            },
          );
        }

        // Hard delete (permanent)
        await apiFetch(
          API_ENDPOINTS.ORGANIZATIONS.PROJECTS.DETAIL(
            selectedOrg.id,
            projectId,
          ),
          {
            method: 'DELETE',
          },
        );
        await refreshProjectData();
      } catch (error) {
        console.error('Failed to delete project:', error);
      }
    },
    [selectedOrg?.id, projects, refreshProjectData],
  );

  const handleRestore = useCallback(
    async (projectId: string) => {
      if (!selectedOrg?.id) return;

      try {
        await apiFetch(
          API_ENDPOINTS.ORGANIZATIONS.PROJECTS.RESTORE(
            selectedOrg.id,
            projectId,
          ),
          {
            method: 'POST',
          },
        );
        await refreshProjectData();
      } catch (error) {
        console.error('Failed to restore project:', error);
      }
    },
    [selectedOrg?.id, refreshProjectData],
  );

  const handleDuplicate = useCallback(
    async (project: Project) => {
      if (!selectedOrg?.id) return;

      try {
        // Call the duplicate endpoint without fiscalYear to duplicate in the same fiscal year
        // The backend will automatically generate a unique name like "Project Name (1)"
        await apiFetch(
          API_ENDPOINTS.ORGANIZATIONS.PROJECTS.COPY_TO_FISCAL_YEAR(
            selectedOrg.id,
            project.id,
          ),
          {
            method: 'POST',
            body: JSON.stringify({}), // Empty body - backend will handle name generation
          },
        );
        // Invalidate all project-related queries to trigger refetch
        await queryClient.invalidateQueries({
          queryKey: ['projects'],
        });
        await queryClient.invalidateQueries({
          queryKey: ['projects-with-subprojects'],
        });
      } catch (error) {
        console.error('Failed to duplicate project:', error);
      }
    },
    [selectedOrg?.id, queryClient],
  );

  const canManageProjs = perms?.canManageProjs || false;

  if (!selectedOrg) return null;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <LandingPageHeader
          organizationName={selectedOrg.name}
          fiscalYears={fiscalYears}
          filteredProjects={projects || []}
          canManageProjs={canManageProjs}
          onProjectClick={() => {}}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <GridContainer>
            <FiscalYearSidebar
              fiscalYears={fiscalYearsWithProjects}
              selectedFiscalYear={selectedFiscalYear}
              onSelectFiscalYear={handleFiscalYearSelect}
            />

            <Box
              sx={{
                gridArea: 'content',
                display: 'flex',
                flexDirection: 'column',
                border: `3px solid ${lp.contentBorder}`,
                borderLeft: 'none',
                borderRadius: '0 16px 0 0',
                backgroundColor: lp.activeLight,
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              <ProjectListHeader
                nameSortOrder={nameSortOrder}
                statusSortOrder={statusSortOrder}
                searchQuery={searchQuery}
                filters={filters}
                availableDepartments={availableDepartments}
                availableFiscalYears={availableFiscalYearsForFilter}
                onNameSort={handleNameSort}
                onStatusSort={handleStatusSort}
                onSearchChange={handleSearchChange}
                onFiltersChange={handleFiltersChange}
              />

              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  px: 0,
                  pb: 0,
                  minHeight: 0,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0,
                }}
              >
                {sortedProjects.map((project, index) => {
                  const isPinned = pinnedProjects.has(project.id);
                  const isOpen = openProjectDropdowns.has(project.id);
                  const { mainStatus } = getProjectStatus(project);

                  // Simple alternation across ALL cards, ensuring last card is always dark
                  const totalCards = sortedProjects.length;
                  let useDarkerColor: boolean;

                  if (totalCards % 2 === 1) {
                    // Odd total cards: start with dark to end with dark
                    useDarkerColor = index % 2 === 0;
                  } else {
                    // Even total cards: start with light to end with dark
                    useDarkerColor = index % 2 === 1;
                  }

                  // Determine card background color based on status
                  let bgColor: string;

                  if (mainStatus === 'Active' || mainStatus === 'Completed') {
                    bgColor = useDarkerColor ? lp.activeDark : lp.activeLight;
                  } else if (
                    mainStatus === 'Pencils Down' ||
                    mainStatus === 'PM Preferred' ||
                    mainStatus === 'BO Approved'
                  ) {
                    bgColor = useDarkerColor
                      ? lp.pencilsDownDark
                      : lp.pencilsDownLight;
                  } else if (mainStatus === 'Archived') {
                    bgColor = useDarkerColor
                      ? lp.archivedDark
                      : lp.archivedLight;
                  } else {
                    bgColor = lp.activeLight;
                  }

                  return (
                    <React.Fragment key={project.id}>
                      <ProjectCard
                        project={project as ProjectWithSubprojects}
                        allProjects={sortedProjects as ProjectWithSubprojects[]}
                        isPinned={isPinned}
                        isOpen={isOpen}
                        onTogglePin={handleTogglePin}
                        onToggleDropdown={handleToggleDropdown}
                        onMenuClick={() => {}}
                        onClick={() => {}}
                        bgColor={bgColor}
                        hoverColor={bgColor}
                        borderStyle={`1px solid ${alpha(theme.palette.info.main, 0.3)}`}
                        isSmallScreen={isSmallScreen}
                        perms={perms}
                        orgId={selectedOrg.id}
                        onArchive={handleArchive}
                        onRestore={handleRestore}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                        onEdit={handleEditProject}
                        onRefresh={refetchProjects}
                        userPrefs={userPrefs}
                        updatePrefs={updatePrefs}
                      />
                    </React.Fragment>
                  );
                })}
              </Box>
            </Box>
          </GridContainer>
        </Box>
      </Box>
    </>
  );
}
